import { createPortal } from 'react-dom';
import '@aleph/shared-components/scss/create-overlay.scss';
import Fail from 'wb-reddit/assets/fail.svg';
import Success from 'wb-reddit/assets/success.svg';
import {
	AppEnum,
	ArrayElement,
	BanIcon,
	Button,
	ButtonAppearance,
	ButtonColor,
	ButtonSize,
	Checkbox,
	CheckIcon,
	Dialog, handleErrorNotification,
	LoaderBars,
	TriangleDown,
	TriangleRight,
	WarningSign,
} from '@aleph/shared-components';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames';
import {
	CreateAdAccountsDocument,
	SpotifyAdAccountsByBusinessQuery,
	SpotifyAdAccountsByBusinessDocument,
	SpotifyLinkUserGroupsToClientDocument,
} from 'wb-spotify/graphql/generated';
import {
	AuthGraphGetUserGroupsAccessDocument,
	AuthGraphAdvertiserOwnersDocument,
} from 'wb-spotify/graphql/authgraph/generated';
import { Placeholder } from 'rsuite';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import { useTranslation } from 'react-i18next';

enum OAuthStatusResponse {
	SUCCESS = 'success',
	FAIL = 'fail',
}

type OAuthResponse = {
	status: OAuthStatusResponse,
};

const CreateOverlayContainer = () => {
	const [parsedOAuthResponse, setParsedOAuthResponse] = useState<OAuthResponse | null>(null);
	const { t } = useTranslation();

	const [openBusinesses, setOpenBusinesses] = useState<string[]>([]);
	const [selectedAdAccounts, setSelectedAdAccounts] = useState<string[]>([]);
	const [searchQuery, setSearchQuery] = useState('');

	const [getAdvertiser] = useLazyQuery(AuthGraphAdvertiserOwnersDocument);
	const [getUserGroupAccess] = useLazyQuery(AuthGraphGetUserGroupsAccessDocument);
	const [linkGroup] = useMutation(SpotifyLinkUserGroupsToClientDocument, {
		errorPolicy: 'all',
	});
	const platformUser = useUserMetaData(AppEnum.SPOTIFY);

	const toggleBusiness = (businessId: string) => {
		if (openBusinesses.includes(businessId)) {
			setOpenBusinesses(openBusinesses.filter(business => business !== businessId));
		} else {
			setOpenBusinesses([...openBusinesses, businessId]);
		}
	};

	const toggleSelectedAdAccount = (adAccountId: string) => {
		if (selectedAdAccounts.includes(adAccountId)) {
			setSelectedAdAccounts(curr => curr.filter(business => business !== adAccountId));
		} else {
			if (!openBusinesses.includes(adAccountId)) {
				toggleBusiness(adAccountId);
			}

			setSelectedAdAccounts(curr => [...curr, adAccountId]);
		}
	};

	const toggleBusinessSelected = (business:
	ArrayElement<SpotifyAdAccountsByBusinessQuery['adAccountsByBusinessLive']>) => {
		if (business?.adAccounts?.every(adAccount => selectedAdAccounts.includes(adAccount?.adAccountId ?? ''))) {
			setSelectedAdAccounts(curr => curr
				.filter(adAccount => !business?.adAccounts
					?.some(acc => acc?.adAccountId === adAccount)));
		} else {
			if (!openBusinesses.includes(business?.businessId ?? '')) {
				toggleBusiness(business?.businessId ?? '');
			}

			setSelectedAdAccounts(curr => [
				...curr,
				...(business?.adAccounts?.map(acc => acc?.adAccountId ?? '') ?? [])]);
		}
	};

	const [searchParams] = useSearchParams();

	const status = searchParams.get('status');
	const spUserId = useMemo(() => searchParams.get('spUserId'), []);
	const advertiserAccountId = useMemo(() => searchParams.get('advertiserAccountId'), []);

	const [createOAuthAdAccounts,
		{
			loading: saving,
			error: createError,
		}] = useMutation(CreateAdAccountsDocument, {
		errorPolicy: 'all',
	});
	const { data, loading } = useQuery(SpotifyAdAccountsByBusinessDocument, {
		variables: {
			spUserId: spUserId ?? '',
		},
		skip: !spUserId,
	});

	const filteredBusinesses = useMemo(() => {
		const list = data?.adAccountsByBusinessLive ?? [];
		const query = searchQuery.trim().toLowerCase();
		if (!query) return list;

		return list
			.map(business => {
				if (!business) return null;
				const businessMatches = business.businessName?.toLowerCase().includes(query);
				const matchingAdAccounts = business.adAccounts?.filter(
					(ad): ad is NonNullable<typeof ad> => ad != null && ad.adAccountName?.toLowerCase().includes(query),
				);
				const hasMatchingAdAccount = (matchingAdAccounts?.length ?? 0) > 0;
				if (!businessMatches && !hasMatchingAdAccount) return null;
				if (businessMatches) return business;

				return {
					...business,
					adAccounts: matchingAdAccounts ?? [],
				};
			})
			.filter((b): b is NonNullable<typeof b> => b != null);
	}, [data?.adAccountsByBusinessLive, searchQuery]);

	useEffect(() => {
		if (window.location.pathname === '/OAuth') {
			if (status) {
				localStorage.setItem('spotifyOAuthResponse', JSON.stringify({
					status,
				}));
			}
		}
	}, [status, spUserId, advertiserAccountId]);

	useEffect(() => {
		const spotifyOAuthResponse = localStorage.getItem('spotifyOAuthResponse');
		const parsed = spotifyOAuthResponse
			? JSON.parse(spotifyOAuthResponse)
			: null;

		setParsedOAuthResponse(parsed);
	}, []);

	const removeSpotifyResponse = () => {
		localStorage.removeItem('spotifyOAuthResponse');
		setParsedOAuthResponse(null);
	};

	const closeModal = () => {
		removeSpotifyResponse();
		window.location.href = '/';
	};

	const OAuthOverlayInfo = {
		[OAuthStatusResponse.SUCCESS]: {
			svg:         Success,
			title:       t('sp: acc_connection.title_success', 'Account successfully connected'),
			description: t('sp: acc_connection.body_success', 'Onwards and upwards!'),
			buttons:     <Button
				title={t('sp: continue', 'Continue')}
				size={ButtonSize.LARGE}
				onClick={removeSpotifyResponse}
				color={ButtonColor.GREEN}
			/>,
		},
		[OAuthStatusResponse.FAIL]: {
			svg:         Fail,
			title:       t('sp: acc_connection.title_err', 'Connection failed; you don’t have admin permissions'),
			description: t('sp: acc_connection.title_body', 'Please contact the person who owns this Spotify account.'),
			buttons:     <>
				<Button
					title={t('sp: back_dashboard', 'Back to dashboard')}
					size={ButtonSize.LARGE}
					appearance={ButtonAppearance.OUTLINED}
					color={ButtonColor.WISE_BLUE}
					onClick={removeSpotifyResponse}
				/>
				<Button
					title={t('sp: see_instructions', 'See instructions')}
					size={ButtonSize.LARGE}
					onClick={removeSpotifyResponse}
				/>
			</>,
		},
	};

	/*
    * Connects the user groups of the advertiser owners to the newly created ad accounts
       mutationData.createAdAccounts.map(account => account.id)
		   getAdvertiser(advertiserAccountId its list) -> owner user id
		   -> getUserGroupAccess(this owner user id and current userid)
		   linkUserGroupsToClient(user groups)
   */
	const connectGroupsToAdAcc = async (adAccountIds: string[], clientIds: string[]) => {
		const { data: advertisersData, error: advErr } = await getAdvertiser({
			variables: {
				advertiserId: adAccountIds,
			},
		});

		if (advErr) {
			handleErrorNotification(advErr);

			return;
		}

		const ownerUserIds = advertisersData?.advertiserAccounts?.items?.map(item => item?.owner?.id) ?? [];

		const { data: userGroupsData, error: accessErr } = await getUserGroupAccess({
			variables: {
				userIds: [...ownerUserIds, platformUser?.userId ?? ''],
			},
		});

		if (accessErr) {
			handleErrorNotification(accessErr);

			return;
		}

		const privateUserGroupIds = userGroupsData?.userGroups?.items?.map(item => item?.userGroupId) ?? [];

		// eslint-disable-next-line no-restricted-syntax
		for (const id of clientIds) {
			// eslint-disable-next-line no-await-in-loop
			const { errors: linkErrs } = await linkGroup({
				variables: {
					clientId:     id,
					userGroupIds: privateUserGroupIds,
				},
			});

			if (linkErrs) {
				handleErrorNotification(linkErrs);

				return;
			}
		}
	};

	const importAccounts = async () => {
		const { errors, data: mutationData } = await createOAuthAdAccounts({
			variables: {
				spUserId:            spUserId ?? '',
				advertiserAccountId: advertiserAccountId ?? '',
				businessId:          data?.adAccountsByBusinessLive?.find(
					business => business?.adAccounts
						?.some(adAccount => selectedAdAccounts
							.includes(adAccount?.adAccountId ?? '')),
				)?.businessId ?? '',
				adAccountIds: selectedAdAccounts,
			},
		});

		if (!errors) {
			localStorage.setItem('spotifyOAuthResponse', JSON.stringify({
				status: OAuthStatusResponse.SUCCESS,
			}));
			window.location.href = '/';
		}

		const accountIds = mutationData?.createAdAccounts?.map(account => account.id) ?? [];
		const clientIds = mutationData?.createAdAccounts?.map(account => account.client.clientId) ?? [];

		await connectGroupsToAdAcc(accountIds, clientIds);
	};

	if (spUserId) {
		return (
			<Dialog
				onClose={removeSpotifyResponse}
				defaultOpen
			>
				<Dialog.Header
					preset={{
						title: t('sp: acc_connection.select', 'Select account(s) to connect'),
					}}
				/>
				<Dialog.Content>
					{createError
						&& <div className="flex items-center gap-[8px] border-b border-b-gray-200 pb-[16px]">
							<WarningSign
								width={32}
								height={32}
								fill="hsl(var(--red-600))"
							/>
							{t('sp: acc_connection.already_connected', 'One of the selected accounts already exists in our system, or something else went wrong.')}
						</div>
					}

					{!loading && (
						<div className="px-[12px] pb-[12px] border-b border-b-gray-200">
							<input
								type="text"
								placeholder={t('sp: acc_connection.search_placeholder', 'Search businesses and accounts...')}
								value={searchQuery}
								onChange={e => setSearchQuery(e.target.value)}
								className="w-full h-[40px] px-[12px] text-[14px] rounded-[4px] border border-gray-300
									placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>
						</div>
					)}

					<div className="flex-1 overflow-y-auto">
						{loading
							&& <div className="flex flex-col gap-[1px] px-[12px]">
								{new Array(5).fill(
									<Placeholder.Graph
										active
										className="w-full !h-[24px] !my-[12px] !rounded-[4px]"
									/>,
								)
								}
							</div>
						}
						{!loading && filteredBusinesses.length === 0 && (
							<div className="px-[12px] py-[24px] text-[14px] text-gray-500 text-center">
								{searchQuery.trim()
									? t('sp: acc_connection.no_results', 'No businesses or accounts match your search.')
									: t('sp: acc_connection.no_accounts', 'No ad accounts available.')}
							</div>
						)}
						{filteredBusinesses.map((business, idx) => (business ? (
							<>
								<div
									key={idx}
									className={classNames(
										'flex items-center justify-between gap-[8px] px-[12px] h-[48px]',
										'rounded-[4px] border-b border-b-white',
										{
											'bg-blue-100': business.adAccounts
												?.some(adAccount => selectedAdAccounts
													.includes(adAccount?.adAccountId ?? '')),
										},
									)}
								>
									<button
										onClick={() => toggleBusiness(business?.businessId)}
										className="flex items-center gap-[16px]"
									>
										<div>
											{openBusinesses.includes(business?.businessId) ? <TriangleDown
												width={8}
												height={8}
											/> : <TriangleRight
												width={8}
												height={8}
											/>
											}
										</div>

										<div className="text-black text-[16px]">{business?.businessName}</div>
									</button>
									<div className="flex gap-[24px]">
										<div
											className="px-[4px] text-[12px] font-bold text-white
													rounded-[4px] bg-gray-700"
										>
											{business?.adAccounts?.length}
										</div>

										<Checkbox
											onClick={() => toggleBusinessSelected(business)}
											checked={
												(business?.adAccounts?.length > 0) && business?.adAccounts
													?.every(adAccount => selectedAdAccounts
														.includes(adAccount?.adAccountId ?? ''))
											}
											partial={
												(business?.adAccounts?.length > 0) && business?.adAccounts
													?.some(adAccount => selectedAdAccounts
														.includes(adAccount?.adAccountId ?? ''))
											}
										/>
									</div>
								</div>
								{openBusinesses.includes(business?.businessId)
										&& <div className="flex w-full">
											<div className="w-[32px] py-[8px] flex justify-center">
												<div className="bg-blue-100 rounded-[4px] w-[2px] h-full"/>
											</div>

											<div className="flex-1">
												{business?.adAccounts?.map((adAccount, accIdx) => (
													<div
														key={accIdx}
														onClick={() => toggleSelectedAdAccount(adAccount?.adAccountId ?? '')}
														className={
															classNames(
																'flex items-center rounded-[4px] justify-between',
																'gap-[8px] px-[12px] h-[32px] border-b border-b-white',
																'focus:outline-0 cursor-pointer group hover:bg-gray-100',
																{
																	'bg-blue-100 hover:!bg-blue-200':
																			selectedAdAccounts
																				.includes(adAccount?.adAccountId ?? ''),
																},
															)
														}
													>
														<div className="text-black text-[14px]">
															{adAccount?.adAccountName}
														</div>

														<Checkbox
															checked={selectedAdAccounts
																.includes(adAccount?.adAccountId ?? '')}
															onClick={
																() => toggleSelectedAdAccount(
																	adAccount?.adAccountId ?? '',
																)
															}
														/>
													</div>
												))
												}
											</div>
										</div>
								}
							</>
						) : null))
						}
					</div>
				</Dialog.Content>
				<Dialog.Footer>
					{!saving
						&& <Button
							title={t('sc: cancel', 'Cancel')}
							leftNode={<BanIcon />}
							appearance={ButtonAppearance.GHOST}
							color={ButtonColor.GRAY}
							onClick={closeModal}
						/>
					}

					<Button
						title={t('sp: acc_connection.import', 'Import accounts')}
						leftNode={saving ? <LoaderBars className="animate-spin"/> : <CheckIcon />}
						disabled={selectedAdAccounts.length === 0 || saving}
						onClick={importAccounts}
					/>
				</Dialog.Footer>
			</Dialog>
		);
	}

	return (parsedOAuthResponse && createPortal(
		<div
			className="flex flex-col gap-[24px] top-0 items-center
		justify-center absolute w-full h-full bg-white/50 backdrop-blur-md"
			style={{ zIndex: 2000 }}
		>
			<div className="flex items-center justify-center">
				<img
					alt={parsedOAuthResponse.status}
					src={OAuthOverlayInfo[parsedOAuthResponse.status].svg}
					className="h-[208px] w-[208px]"
				/>
			</div>
			<div className="flex flex-col items-center justify-center gap-[8px]">
				<div
					className="text-[18px] font-bold"
				>
					{OAuthOverlayInfo[parsedOAuthResponse.status].title}
				</div>

				<div className="text-[18px] text-[#666]">
					{OAuthOverlayInfo[parsedOAuthResponse.status].description}
				</div>
			</div>
			<div className="flex flex-row items-center justify-center gap-[16px] pt-2">
				{OAuthOverlayInfo[parsedOAuthResponse.status].buttons}
			</div>
		</div>,
		document.body,
	));
};

export default CreateOverlayContainer;
